import { useState } from "react";
import api, { eur, eurFull } from "@/lib/api";
import { ScoreGauge } from "@/components/ScoreGauge";
import PackageReco from "@/components/PackageReco";
import CallScript from "@/components/company/CallScript";
import SequenceStatus from "@/components/company/SequenceStatus";
import DocuSignPanel from "@/components/DocuSignPanel";
import { Buildings, Calculator, Lightning, Sparkle, Crosshair, EnvelopeSimple, LinkedinLogo } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Field, dirigeantLinkedin } from "./helpers";
import { ContactEditor, TagsFolders, GeneratedDocs } from "./cards";

function IdentityColumn({ c, reload, onUseInEmail, setLoiTerms, loiTerms, emailLoading, findEmailPro }) {
  return (
    <div className="space-y-6">
      <div className="bg-white border border-border p-5">
        <div className="flex items-center gap-2 mb-3"><Buildings size={16} className="text-primary" /><span className="overline">Fiche cabinet</span></div>
        <Field label="Adresse" value={c.adresse} />
        <Field label="Site internet" value={c.site} />
        <Field label="LinkedIn" value={c.linkedin} />
        <Field label="Date de création" value={c.date_creation} mono />
        <Field label="Salariés" value={c.effectif_label || c.nb_salaries} mono />
        <Field label="État administratif" value={c.etat_administratif === "A" ? "Actif" : c.etat_administratif === "C" ? "Cessé" : c.etat_administratif} />
        {(c.dirigeants_detail && c.dirigeants_detail.length > 0) ? (
          <div className="py-2 border-b border-border">
            <div className="overline text-muted-foreground mb-1">Dirigeant(s)</div>
            {c.dirigeants_detail.map((d, i) => {
              const fullName = `${d.prenoms || ""} ${d.nom || ""}`.trim();
              return (
                <div key={i} className="text-sm flex items-center justify-between gap-2" data-testid="dirigeant-row">
                  <a href={dirigeantLinkedin(fullName, c.ville, c.linkedin_dirigeant, i === 0)} target="_blank" rel="noreferrer"
                    data-testid="dirigeant-linkedin" title="Voir le profil LinkedIn"
                    className="inline-flex items-center gap-1.5 min-w-0 group">
                    <LinkedinLogo size={15} weight="fill" className="text-[#0A66C2] shrink-0" />
                    <span className="truncate group-hover:underline group-hover:text-primary">{fullName}</span>
                    <span className="text-xs text-muted-foreground shrink-0">· {d.qualite}</span>
                  </a>
                  {d.age && <span className="font-mono text-xs shrink-0">{d.age} ans</span>}
                </div>
              );
            })}
          </div>
        ) : (
          <>
            {(c.dirigeants && c.dirigeants.length > 0) ? (
              <div className="py-2 border-b border-border">
                <div className="overline text-muted-foreground mb-1">Dirigeant(s)</div>
                {c.dirigeants.map((nom, i) => (
                  <a key={i} href={dirigeantLinkedin(nom, c.ville, c.linkedin_dirigeant, i === 0)} target="_blank" rel="noreferrer"
                    data-testid="dirigeant-linkedin" title="Voir le profil LinkedIn"
                    className="flex items-center gap-1.5 text-sm group">
                    <LinkedinLogo size={15} weight="fill" className="text-[#0A66C2] shrink-0" />
                    <span className="truncate group-hover:underline group-hover:text-primary">{nom}</span>
                  </a>
                ))}
              </div>
            ) : <Field label="Dirigeant(s)" value="—" />}
            <Field label="Âge dirigeant" value={`${c.age_dirigeant} ans`} mono />
          </>
        )}
        <Field label="Capital social" value={eurFull(c.capital_social)} mono />
        {c.data_sources && c.data_sources.length > 0 && (
          <div className="pt-3 flex flex-wrap items-center gap-1" data-testid="data-sources">
            <span className="overline text-muted-foreground mr-1">Sources</span>
            {c.data_sources.map((s) => <span key={s} className="text-[10px] uppercase tracking-wide border border-border bg-secondary px-1.5 py-0.5 rounded">{s}</span>)}
          </div>
        )}
        {c.pappers_url && (
          <a href={c.pappers_url} target="_blank" rel="noreferrer" data-testid="pappers-link"
            className="mt-3 inline-flex items-center gap-1.5 text-sm text-primary font-semibold hover:underline">
            <Buildings size={14} /> Voir la fiche Pappers ↗
          </a>
        )}
      </div>
      {c.contacts && (
        <div className="bg-white border border-border p-5" data-testid="contacts-block">
          <div className="flex items-center gap-2 mb-3"><EnvelopeSimple size={16} className="text-primary" /><span className="overline">Contacts {c.contacts.site_discovered && <span className="text-muted-foreground normal-case">· site trouvé auto.</span>}</span></div>
          {c.contacts.site && <Field label="Site web" value={c.contacts.site} />}
          {c.contacts.emails && c.contacts.emails.length > 0 && (
            <div className="py-2 border-b border-border">
              <div className="overline text-muted-foreground mb-1">Emails</div>
              {c.contacts.emails.map((e) => <a key={e} href={`mailto:${e}`} data-testid="contact-email" className="block text-sm text-primary hover:underline break-all">{e}</a>)}
            </div>
          )}
          {c.contacts.telephones && c.contacts.telephones.length > 0 && (
            <div className="py-2 border-b border-border">
              <div className="overline text-muted-foreground mb-1">Téléphones</div>
              {c.contacts.telephones.map((t) => <a key={t} href={`tel:${t}`} data-testid="contact-phone" className="inline-block mr-3 text-sm text-primary hover:underline font-mono">{t}</a>)}
            </div>
          )}
          {c.contacts.orias && c.contacts.orias.length > 0 && <Field label="N° ORIAS" value={c.contacts.orias.join(", ")} mono />}
          {c.contacts.emails_dirigeants_estimes && c.contacts.emails_dirigeants_estimes.length > 0 && (
            <div className="pt-3">
              <div className="overline text-muted-foreground mb-1">Emails dirigeants (estimés)</div>
              {c.contacts.emails_dirigeants_estimes.map((d, i) => (
                <div key={i} className="mb-2" data-testid="dirigeant-email-guess">
                  <div className="text-sm font-semibold">{d.nom}</div>
                  {d.emails_probables.map((e) => <a key={e} href={`mailto:${e}`} className="block text-xs text-muted-foreground hover:text-primary hover:underline break-all">{e}</a>)}
                </div>
              ))}
              <p className="text-[11px] text-muted-foreground mt-1">⚠️ Emails déduits du nom + domaine — à vérifier avant envoi.</p>
            </div>
          )}
        </div>
      )}
      <div className="bg-white border border-border p-5" data-testid="dirigeant-contacts-block">
        <div className="flex items-center justify-between mb-3">
          <span className="overline">Contact dirigeant (vérifié)</span>
        </div>
        {c.email_pro_dirigeant ? (
          <div className="py-2 border-b border-border">
            <div className="overline text-muted-foreground mb-1">Email pro dirigeant</div>
            <a href={`mailto:${c.email_pro_dirigeant}`} data-testid="email-pro-dirigeant" className="text-sm text-primary hover:underline break-all">{c.email_pro_dirigeant}</a>
            {(c.email_status || c.email_qualification) && (
              <span className={`ml-2 text-[10px] uppercase tracking-wide border px-1.5 py-0.5 rounded-full ${c.email_status === "élevé" || c.email_qualification ? "bg-emerald-50 text-emerald-700 border-emerald-200" : c.email_status === "moyen" ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>
                {c.email_qualification || c.email_status}
              </span>
            )}
            {c.email_method && <div className="text-[11px] text-muted-foreground mt-1">Méthode : {c.email_method}</div>}
          </div>
        ) : (
          <button data-testid="find-email-pro-btn" onClick={findEmailPro} disabled={emailLoading}
            className="w-full mb-2 px-3 py-2 text-sm font-semibold bg-foreground text-white hover:opacity-90 disabled:opacity-50">
            {emailLoading ? "Recherche email pro (gratuit)…" : "✉️ Trouver l'email pro (gratuit)"}
          </button>
        )}
        {c.linkedin_dirigeant && (
          <div className="py-2 border-b border-border">
            <div className="overline text-muted-foreground mb-1">LinkedIn dirigeant</div>
            <a href={c.linkedin_dirigeant.startsWith("http") ? c.linkedin_dirigeant : `https://${c.linkedin_dirigeant}`} target="_blank" rel="noreferrer" data-testid="linkedin-dirigeant" className="text-sm text-primary hover:underline break-all">{c.linkedin_dirigeant} ↗</a>
          </div>
        )}
      </div>
      {c.bodacc_events && c.bodacc_events.length > 0 && (
        <div className="bg-white border border-border p-5" data-testid="bodacc-block">
          <div className="overline text-muted-foreground mb-3">Vie juridique · BODACC ({c.bodacc_events.length})</div>
          <div className="space-y-2">
            {c.bodacc_events.slice(0, 6).map((e, i) => {
              const isCession = e.famille === "cession" || e.famille === "procedure" || e.famille === "radiation";
              return (
                <a key={i} href={e.url} target="_blank" rel="noreferrer" data-testid="bodacc-event"
                  className="flex items-start gap-2 text-xs hover:underline">
                  <span className={`mt-0.5 inline-block w-1.5 h-1.5 rounded-full shrink-0 ${isCession ? "bg-red-500" : "bg-slate-300"}`} />
                  <span className="font-mono text-muted-foreground shrink-0">{e.date}</span>
                  <span className={isCession ? "font-semibold text-red-700" : ""}>{e.titre}</span>
                </a>
              );
            })}
          </div>
        </div>
      )}
      <div className="bg-white border border-border p-5">
        <div className="overline text-muted-foreground mb-3">Données financières</div>
        <Field label="Chiffre d'affaires" value={eurFull(c.ca)} mono />
        <Field label="Commissions" value={eurFull(c.commissions)} mono />
        <Field label="Résultat" value={eurFull(c.resultat)} mono />
        <Field label="Croissance" value={`${c.croissance}%`} mono />
        <div className="mt-3 flex flex-wrap gap-1">
          {(c.compagnies || []).map((co) => <span key={co} className="text-[11px] border border-border px-2 py-0.5">{co}</span>)}
        </div>
      </div>
      <ContactEditor c={c} reload={reload} />
      <TagsFolders c={c} reload={reload} />
      <PackageReco c={c} onUseInEmail={onUseInEmail} onUseInLoi={(t) => setLoiTerms({ ...t })} />
      <SequenceStatus c={c} />
      <CallScript c={c} reload={reload} onUseInEmail={onUseInEmail} />
      <GeneratedDocs c={c} />
      <DocuSignPanel c={c} initialTerms={loiTerms} />
    </div>
  );
}

function ScoringColumn({ c, synergies, synLoading, calcSynergies }) {
  const v = c.valuation;
  return (
    <div className="space-y-6">
      {c.transmission_signal && (
        <div className="bg-white border border-border p-5" data-testid="transmission-signal">
          {(() => {
            const sig = c.transmission_signal;
            const tone = sig.niveau === "radié" ? { bar: "bg-red-700", txt: "text-red-700", bg: "bg-red-100 border-red-300" }
              : sig.niveau === "élevé" ? { bar: "bg-red-500", txt: "text-red-700", bg: "bg-red-50 border-red-200" }
              : sig.niveau === "modéré" ? { bar: "bg-amber-500", txt: "text-amber-700", bg: "bg-amber-50 border-amber-200" }
              : { bar: "bg-emerald-500", txt: "text-emerald-700", bg: "bg-emerald-50 border-emerald-200" };
            return (
              <>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2"><Crosshair size={16} className="text-foreground" /><span className="overline">Signal de transmission</span></div>
                  <span className={`text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 border rounded-full ${tone.bg} ${tone.txt}`}>{sig.niveau}</span>
                </div>
                <div className="flex items-end gap-2 mb-2">
                  <span className={`font-mono text-3xl font-bold ${tone.txt}`}>{sig.score}</span>
                  <span className="text-sm text-muted-foreground mb-1">/100</span>
                </div>
                <div className="h-2 bg-secondary rounded-full overflow-hidden mb-4">
                  <div className={`h-full ${tone.bar}`} style={{ width: `${sig.score}%` }} />
                </div>
                {sig.facteurs && sig.facteurs.length > 0 ? (
                  <ul className="space-y-1.5">
                    {sig.facteurs.map((f, i) => (
                      <li key={i} className="text-xs flex items-start justify-between gap-2" data-testid="signal-factor">
                        <span className="flex items-start gap-1.5">
                          <span className={`mt-1 w-1.5 h-1.5 rounded-full shrink-0 ${f.type === "fort" ? "bg-red-500" : f.type === "modéré" ? "bg-amber-500" : "bg-slate-300"}`} />
                          {f.label}
                        </span>
                        <span className="font-mono text-muted-foreground">+{f.poids}</span>
                      </li>
                    ))}
                  </ul>
                ) : <p className="text-xs text-muted-foreground">Aucun signal détecté. Lancez « 360° » pour trianguler les sources.</p>}
              </>
            );
          })()}
        </div>
      )}
      <div className="bg-white border border-border p-5">
        <div className="overline text-muted-foreground mb-4">Scoring IA</div>
        <div className="grid grid-cols-3 gap-1">
          <ScoreGauge value={c.scores.transmission} label="Transmission" />
          <ScoreGauge value={c.scores.acquisition} label="Acquisition" />
          <ScoreGauge value={c.scores.risk} label="Risque" invert />
        </div>
        <div className="mt-3 text-xs text-muted-foreground text-center">Probabilité de cession · Attractivité · Niveau de risque</div>
      </div>

      <div className="bg-white border border-border p-5">
        <div className="flex items-center gap-2 mb-4"><Calculator size={16} className="text-primary" /><span className="overline">Valorisation</span></div>
        <div className="grid grid-cols-3 gap-2 mb-4 text-center">
          <div className="border border-border p-3"><div className="overline text-muted-foreground">Basse</div><div className="font-mono text-base font-semibold">{eur(v.low)}</div></div>
          <div className="border-2 border-primary p-3 bg-accent"><div className="overline text-primary">Médiane</div><div className="font-mono text-lg font-bold text-primary">{eur(v.median)}</div></div>
          <div className="border border-border p-3"><div className="overline text-muted-foreground">Haute</div><div className="font-mono text-base font-semibold">{eur(v.high)}</div></div>
        </div>
        <div className="space-y-1.5 text-xs">
          {Object.entries(v.methods).map(([m, vals]) => (
            <div key={m} className="flex justify-between border-b border-border py-1.5">
              <span className="capitalize text-muted-foreground">Multiple {m}</span>
              <span className="font-mono">{eur(vals.low)} – {eur(vals.high)}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="ai-surface p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2"><Lightning size={16} className="text-[#6366f1]" weight="fill" /><span className="overline text-[#4f46e5]">Synergies IA</span></div>
          <button data-testid="synergies-btn" onClick={calcSynergies} disabled={synLoading} className="text-xs font-semibold text-[#4f46e5] hover:underline">{synLoading ? "Calcul…" : "Calculer"}</button>
        </div>
        {synergies ? (
          <div className="space-y-2 text-sm">
            {["commerciales", "operationnelles", "financieres"].map((k) => synergies[k] && (
              <div key={k} className="border-b border-[#c7d2fe] pb-2">
                <div className="flex justify-between"><span className="capitalize font-medium">{k}</span><span className="font-mono text-[#4f46e5]">{eur(synergies[k].montant_estime)}</span></div>
                <div className="text-xs text-muted-foreground">{synergies[k].description}</div>
              </div>
            ))}
            <div className="flex justify-between pt-1 font-semibold"><span>Total annuel estimé</span><span className="font-mono text-[#4f46e5]">{eur(synergies.total_annuel)}</span></div>
          </div>
        ) : <p className="text-xs text-muted-foreground">Estimez les synergies commerciales, opérationnelles et financières.</p>}
      </div>
    </div>
  );
}

function AnalysisColumn({ c, analyzing, analyze }) {
  return (
    <div>
      <div className="ai-surface p-5 sticky top-36">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2"><Sparkle size={16} className="text-[#6366f1]" weight="fill" /><span className="overline text-[#4f46e5]">Analyse IA · SWOT</span></div>
        </div>
        {!c.ai_analysis && (
          <div className="text-center py-6">
            <p className="text-sm text-muted-foreground mb-4">Générez l'analyse stratégique du cabinet.</p>
            <button data-testid="analyze-btn" onClick={analyze} disabled={analyzing}
              className="px-4 py-2.5 bg-[#6366f1] text-white text-sm font-semibold hover:bg-[#4f46e5] disabled:opacity-60">
              {analyzing ? "Analyse en cours…" : "Analyser ce cabinet"}
            </button>
          </div>
        )}
        {c.ai_analysis && (
          <div className="space-y-4 text-sm" data-testid="ai-analysis">
            <div>
              <div className="overline text-muted-foreground mb-1">Résumé exécutif</div>
              <p className="text-sm leading-relaxed">{c.ai_analysis.resume_executif}</p>
            </div>
            {[["points_forts", "Points forts", "#059669"], ["points_faibles", "Points faibles", "#DC2626"], ["opportunites", "Opportunités", "#002FA7"], ["menaces", "Menaces", "#D97706"]].map(([k, label, col]) => (
              (c.ai_analysis[k] || []).length > 0 && (
                <div key={k}>
                  <div className="overline mb-1" style={{ color: col }}>{label}</div>
                  <ul className="space-y-1">{c.ai_analysis[k].map((x, i) => <li key={i} className="text-xs flex gap-1.5"><span style={{ color: col }}>•</span>{x}</li>)}</ul>
                </div>
              )
            ))}
            <button onClick={analyze} disabled={analyzing} data-testid="analyze-btn" className="text-xs text-[#4f46e5] hover:underline">{analyzing ? "…" : "Régénérer"}</button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function SyntheseTab({ c, reload, onUseInEmail }) {
  const [analyzing, setAnalyzing] = useState(false);
  const [synergies, setSynergies] = useState(null);
  const [synLoading, setSynLoading] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [loiTerms, setLoiTerms] = useState(null);

  const findEmailPro = async () => {
    setEmailLoading(true);
    try {
      const { data } = await api.post(`/companies/${c.id}/find-email-pro`);
      await reload();
      if (data.found) toast.success(`Email pro : ${data.email_pro_dirigeant} · confiance ${data.confidence} (${data.method})`);
      else toast.warning(data.reason || "Aucun email pro trouvé.");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur recherche email"); }
    finally { setEmailLoading(false); }
  };
  const analyze = async () => {
    setAnalyzing(true);
    try { await api.post(`/ai/analyze/${c.id}`); await reload(); toast.success("Analyse IA générée"); }
    catch { toast.error("Erreur d'analyse"); } finally { setAnalyzing(false); }
  };
  const calcSynergies = async () => {
    setSynLoading(true);
    try { const { data } = await api.post(`/ai/synergies/${c.id}`); setSynergies(data); }
    catch { toast.error("Erreur synergies"); } finally { setSynLoading(false); }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <IdentityColumn c={c} reload={reload} onUseInEmail={onUseInEmail}
        setLoiTerms={setLoiTerms} loiTerms={loiTerms} emailLoading={emailLoading} findEmailPro={findEmailPro} />
      <ScoringColumn c={c} synergies={synergies} synLoading={synLoading} calcSynergies={calcSynergies} />
      <AnalysisColumn c={c} analyzing={analyzing} analyze={analyze} />
    </div>
  );
}
